import { FLAGS } from '@iheartjane/feature-flags';
import '@iheartjane/reefer/styles/index.css';
import { isbot } from 'isbot';
import compact from 'lodash-es/compact';
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import { data, useLoaderData } from 'react-router';
import { getToast } from 'remix-toast';

import { updateShoppingPreferences } from '@jane/bloom/actions/store.server';
import { Document } from '@jane/bloom/components/document/document';
import { tagManagerScriptSrc } from '@jane/bloom/components/document/googleTagPlatform';
import { getJdid } from '@jane/bloom/dataAccess/dm/client.server';
import { useFallbackStoreResolution } from '@jane/bloom/hooks/storeSwitcher/useFallbackStoreResolution';
import { useTrackInitialStorePin } from '@jane/bloom/hooks/useTrackInitialStorePin';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { faviconTag } from '@jane/bloom/resources/meta/favicon';
import { fontCssTags } from '@jane/bloom/resources/meta/fontCss';
import { themeColor } from '@jane/bloom/resources/meta/themeColor';
import { buildThemeCssVars } from '@jane/bloom/resources/theme';
import { ddTrace } from '@jane/bloom/services/dd-trace.server';
import { env } from '@jane/bloom/services/env.server';
import { getAgeGate } from '@jane/bloom/sessions/ageGate.server';
import { getCartInfo } from '@jane/bloom/sessions/cart.server';
import { getDMSession } from '@jane/bloom/sessions/dm.server';
import {
  getPromoCode,
  setPromoCode,
} from '@jane/bloom/sessions/promoCode.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import { DMRoutes } from '@jane/bloom/types/dmRoutes';
import type { LocalRootLoaderData } from '@jane/bloom/types/schemas/bloom/rootLoader';
import type { RouteHandle } from '@jane/bloom/types/schemas/bloom/routeHandle';
import { getDefaultCart } from '@jane/bloom/utils/cart/getDefaultCart.server';
import { ensureBloomMenu } from '@jane/bloom/utils/ensureBloomMenu.server';
import { stateNameToAbbreviation } from '@jane/bloom/utils/geo/stateNameToAbbreviation';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';
import { promiseHashAllSettled } from '@jane/bloom/utils/requests/promiseHashAllSettled';
import { getCustomLanguage } from '@jane/bloom/viewModels/getCustomLanguage.server';

import defaultFaviconSrc from './assets/favicon.ico';
import stylesLink from './assets/styles.css?url';
import desktopStylesLink from './assets/styles.desktop.css?url';

export const handle: RouteHandle<LocalRootLoaderData> = {
  scripts: ({
    data: {
      appConfig: { headlessEmbedLink },
      googleTagPlatform: { gtmContainerId },
    },
  }) => {
    return compact([
      /** Google Tag Manager - create the script tag directly to ensure it is loaded, ignoring possible React rehydration. */
      gtmContainerId && {
        async: true,
        defer: true,
        src: tagManagerScriptSrc(gtmContainerId),
      },
      /** @see https://docs.iheartjane.com/jane-docs/implementing-roots/embedding-checkout */
      headlessEmbedLink && {
        async: true,
        defer: true,
        id: 'jane-frame-script',
        src: headlessEmbedLink,
      },
    ]);
  },
};

export const links: LinksFunction = () => [
  {
    crossOrigin: 'anonymous',
    href: 'https://fonts.googleapis.com',
    rel: 'preconnect',
  },
  {
    crossOrigin: 'anonymous',
    href: 'https://fonts.gstatic.com',
    rel: 'preconnect',
  },
  { href: stylesLink, rel: 'stylesheet' },
  { href: desktopStylesLink, media: '(min-width: 768px)', rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
  if (!loaderData) return [];

  const faviconSrc = loaderData.faviconUrl ?? defaultFaviconSrc;

  return compact([
    faviconTag(faviconSrc),
    { content: loaderData.buildVersion, name: 'version' },
    { href: `${loaderData.menuUrl}/manifest.json`, rel: 'manifest' },
    themeColor(loaderData.theme.bloom?.colors?.header?.main),
    ...fontCssTags(loaderData.theme.bloom?.fonts),
  ]);
};

async function _loader({ request }: LoaderFunctionArgs) {
  const cookie = request.headers.get('Cookie');
  const menu = await ensureBloomMenu(request);
  const {
    cookie_prefix,
    custom_seo,
    favicon_url,
    footer,
    google_tag_manager_container_id,
    link_to_main_site,
    logo,
    menu_url,
    operator: { id: operatorId, name: operatorName },
    partner_scripts,
    stores,
    theme,
  } = menu;
  const { pinnedStore, shoppingPreferences, wasStoreInitiallyResolved } =
    await globalSubloader(request, menu);

  const isBot = isbot(request.headers.get('user-agent'));

  const url = new URL(request.url);
  const promoCodeParam = url.searchParams.get('promo_code');

  const {
    ageGate,
    cart,
    promoCode,
    remixToast: { headers, toast },
    userIdentity,
  } = await promiseHashAllSettled({
    ageGate: {
      defaultValue: { confirmedAge: false, type: 'yes_no' as const },
      /**
       * @todo Age gate cookie checks are now only a client-side concern.
       * Consider performing this check without the server request, possibly with a root clientLoader.
       */
      promise: getAgeGate(request, theme, cookie_prefix, pinnedStore.id),
    },
    cart: {
      defaultValue: getDefaultCart(),
      promise: getCartInfo(cookie, cookie_prefix),
    },
    promoCode: {
      defaultValue: undefined,
      promise: getPromoCode(cookie, cookie_prefix),
    },
    remixToast: {
      defaultValue: { headers: new Headers(), toast: undefined },
      promise: getToast(request),
    },
    userIdentity: {
      defaultValue: null,
      promise: getUserIdentity(request, cookie_prefix),
    },
  });

  // If promo_code is in URL params, set it in the session
  if (promoCodeParam) {
    const promoCodeCookie = await setPromoCode(cookie_prefix, promoCodeParam);
    headers.append('Set-Cookie', promoCodeCookie);
  }

  const shoppingPreferencesHeaders = await updateShoppingPreferences({
    args: shoppingPreferences,
    cookie,
    cookiePrefix: cookie_prefix,
    // Skip verification when the store was just resolved server-side —
    // it was already validated against menu.stores moments ago.
    verifyStoreId: !wasStoreInitiallyResolved,
  });
  if (shoppingPreferencesHeaders) {
    shoppingPreferencesHeaders.forEach((value, key) => {
      headers.append(key, value);
    });
  }

  const pathname = url.pathname;
  const matches = pathname.split('/');
  const isDmRoute = Object.values(DMRoutes).some((route) =>
    matches.includes(route)
  );
  const {
    isPersisted,
    jdid,
    session: dmSession,
  } = await getJdid({
    cookie,
    cookiePrefix: cookie_prefix,
    shouldGenerate: !isDmRoute,
  });
  if (!isDmRoute) {
    if (!isPersisted) {
      dmSession.set('jdid', jdid);
      const { commitSession } = getDMSession(cookie_prefix);
      headers.append('Set-Cookie', await commitSession(dmSession));
    }
  }

  let reservationMode = '';
  if (pinnedStore?.pickup && pinnedStore?.delivery) {
    reservationMode = `${
      getCustomLanguage(pinnedStore).reservationModeLabels.pickup || 'pickup'
    } and ${
      getCustomLanguage(pinnedStore).reservationModeLabels.delivery ||
      'delivery'
    }`;
  } else if (pinnedStore?.pickup) {
    reservationMode =
      getCustomLanguage(pinnedStore).reservationModeLabels.pickup || 'pickup';
  } else if (pinnedStore?.delivery) {
    reservationMode =
      getCustomLanguage(pinnedStore).reservationModeLabels.delivery ||
      'delivery';
  }

  const fulfillmentMode = pinnedStore?.recreational
    ? pinnedStore?.store_compliance_language_settings?.['recreational'] ||
      'recreational'
    : 'medical';

  const featureFlags = await isFeatureFlagsEnabled({
    flagNames: [
      FLAGS.c3MidGridAdRow,
      FLAGS.obGtagEvents,
      FLAGS.c3SpecialsRedesignMenu,
      FLAGS.c1BundleTag,
      FLAGS.obWizardBannerEnabled,
      FLAGS.c1CustomizeLoginPrompt,
      FLAGS.c3ThematicRowsContext,
    ],
    request,
  });

  const rootData: LocalRootLoaderData = {
    ageGateConfirmed: ageGate.confirmedAge,
    ageGateType: ageGate.type,
    appConfig: {
      datadog: {
        appId: env.datadog.appId,
        debug: env.datadog.debug,
        env: env.datadog.env,
        token: env.datadog.clientToken,
      },
      facebookAppId: env.facebookAppId,
      googlePlacesApiKey: env.googlePlacesApiKey,
      headlessEmbedLink: env.headlessEmbedLink,
      mixpanelProjectToken: env.mixpanelProjectToken,
      moengageWorkspaceId: env.moengageWorkspaceId,
    },
    buildVersion: env.gitCommitSha,
    cart,
    cookiePrefix: cookie_prefix,
    customLanguage: getCustomLanguage(pinnedStore),
    customSEO: custom_seo,
    faviconUrl: favicon_url,
    featureFlags,
    googleTagPlatform: {
      gtmContainerId: google_tag_manager_container_id,
    },
    isBot,
    jdid,
    linkToMainSite: link_to_main_site,
    logo,
    menuFooter: footer,
    menuUrl: menu_url,
    metaProps: {
      fulfillmentMode,
      pinnedStoreCity: pinnedStore?.city ?? '',
      pinnedStoreName: pinnedStore.name || operatorName,
      pinnedStoreState: stateNameToAbbreviation(pinnedStore?.state) ?? '',
      reservationMode,
    },
    nodeEnv: env.nodeEnv,
    operator: {
      id: operatorId,
      name: operatorName,
    },
    partnerScripts: partner_scripts ?? [],
    pinnedStore: pinnedStore,
    pluginVersion: request.headers.get('x-jane-plugin-version') ?? undefined,
    promoCode: promoCodeParam ?? promoCode,
    publicPath: env.publicUrl,
    shoppingPreferences,
    stores,
    theme,
    themeInlineCss: buildThemeCssVars(theme),
    toast,
    wasStoreInitiallyResolved,
    whoami: {
      user: userIdentity?.user || {},
      user_segments: userIdentity?.user_segments || [],
    },
  };

  return data(rootData, { headers });
}

export const loader = (args: LoaderFunctionArgs) =>
  ddTrace.initOnce().trace(
    'loader.root',
    {
      measured: true,
    },
    async () => {
      return await _loader(args);
    }
  );

export default function App() {
  const rootData = useLoaderData<LocalRootLoaderData>();
  useFallbackStoreResolution();
  useTrackInitialStorePin();
  return <Document {...rootData} />;
}
